import { Col, Divider, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { type SdmModuleSpecsFragment } from '../../../../../api/fragments/SdmModuleSpecs';
import {
    CreateSdmModuleDocument,
    type CreateSdmModuleMutation,
    type CreateSdmModuleMutationVariables,
} from '../../../../../api/mutations/createSdmModule';
import { type SalesForceSettingInput, type SdmModuleSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import validators from '../../../../../utilities/validators';
import SdmModulePage, { type SdmModulePageProps } from '../../SdmModulePage';
import { type ModuleImplementation } from './types';

type SdmModuleImplementation = ModuleImplementation<SdmModuleSpecsFragment, SdmModuleSettings & SalesForceSettingInput>;

const formValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('baseUrl'),
    validators.requiredString('clientId'),
    validators.requiredString('clientSecret'),
    validators.requiredString('grantType')
);

const colSpan = { md: 12, xs: 24 };

const Form: SdmModuleImplementation['FormComponent'] = () => {
    const { t } = useTranslation(['moduleDetails']);

    return (
        <>
            <Row gutter={10}>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
            </Row>
            <Divider>{t('moduleDetails:sections.sdmModule.salesForce')}</Divider>
            <Row gutter={10}>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.baseUrl', { returnObjects: true })}
                        name="baseUrl"
                        required
                    />
                </Col>
                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.clientId', { returnObjects: true })}
                        name="clientId"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <PasswordField
                        {...t('moduleDetails:fields.clientSecret', { returnObjects: true })}
                        name="clientSecret"
                        required
                    />
                </Col>

                <Col {...colSpan}>
                    <InputField
                        {...t('moduleDetails:fields.grantType', { returnObjects: true })}
                        name="grantType"
                        required
                    />
                </Col>
            </Row>
        </>
    );
};

const implementation: SdmModuleImplementation = {
    PageComponent: ({ module }: SdmModulePageProps) => <SdmModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.SdmModule.className'),
    FormComponent: Form,
    getValidator: () => formValidator,
    create: async (apolloClient, companyId, { displayName, ...secrets }) =>
        apolloClient
            .mutate<CreateSdmModuleMutation, CreateSdmModuleMutationVariables>({
                mutation: CreateSdmModuleDocument,
                variables: { companyId, settings: { displayName }, secrets },
            })
            .then(({ data }) => data.module),
};

export default implementation;
